import { doc, p } from '@atlaskit/adf-utils/builders';
import { getEmptyADF } from '@atlaskit/adf-utils/empty-adf';
import { GIC_CALLBACK_PAYLOAD_AI_BREAKDOWN_CREATE_ID } from '@atlassian/jira-issue-view-common-constants';
import type { ChildIssueType } from '@atlassian/jira-issue-view-common-types/src/child-issue-type';
import type { GICPayload } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import {
	JSW_ISSUE_VIEW_AI_BREAKDOWN_CHILD_ISSUE,
	JSW_ISSUE_VIEW_AI_BREAKDOWN_SUBTASK,
} from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants';
import type { SuggestedChildIssue } from './context';

type IssueCreateModalData = {
	childIssue: SuggestedChildIssue;
	projectId: string;
	parentIssueId: string;
	childIssueTypes: ChildIssueType[];
	isSubTaskCreationOpen: boolean;
};

export const issueBreakdownGicOptions = {
	minimizableModalView: 'ai-enabled',
	displayCreateAnotherIssueToggle: false,
	disableProjectDropdown: true,
} as const;

export const getIssueCreateModalPayload = (data: IssueCreateModalData): GICPayload => {
	const { childIssue, projectId, childIssueTypes, parentIssueId, isSubTaskCreationOpen } = data;
	const { summary, description, issueTypeId } = childIssue;
	const triggerPointKey = isSubTaskCreationOpen
		? JSW_ISSUE_VIEW_AI_BREAKDOWN_SUBTASK
		: JSW_ISSUE_VIEW_AI_BREAKDOWN_CHILD_ISSUE;

	const descriptionAdf = { adfValue: description ? doc(p(description)) : getEmptyADF() };
	return {
		...issueBreakdownGicOptions,
		payload: {
			project: { projectId },
			issueType: { issueTypeId: issueTypeId.toString() },
			issueTypes: childIssueTypes.map(({ id }) => id).join(', '),
			defaultValues: {
				singleLineTextFields: [{ fieldId: 'summary', text: summary }],
				richTextFields: [{ fieldId: 'description', richText: descriptionAdf }],
				parentField: { issueId: parentIssueId },
			},
			// parentId is needed for subtask creation, parentField needed for child issues
			parentId: { parentId: parentIssueId },
		},
		callbackPayload: {
			id: GIC_CALLBACK_PAYLOAD_AI_BREAKDOWN_CREATE_ID,
			data: { wasFormDirty: false },
		},
		triggerPointKey,
		isSubTaskCreationOpen,
	};
};

export const getSelectedIssueType = (
	childIssueTypes: ChildIssueType[],
	issueTypeId: string,
): ChildIssueType | undefined => childIssueTypes.find(({ id }) => id === issueTypeId) || undefined;

export const mapChildIssueTypesToNumberIds = (childIssueTypes: ChildIssueType[]): number[] =>
	childIssueTypes.map((issueType) => Number(issueType.id));
